import { Link } from "react-router-dom";
import type { Video } from "@/types/video";
import { highlight } from "@/utils/video";

interface VideoCardProps {
  video: Video;
  keyword?: string;
  onToggleFavorite?: (id: number) => void;
  isFavorite?: boolean;
}

function HighlightText({ text, keyword }: { text: string; keyword?: string }) {
  if (!keyword) return <>{text}</>;
  const parts = highlight(text, keyword);
  return (
    <>
      {parts.map((p, i) =>
        p.hit ? (
          <mark key={i} className="rounded-sm bg-primary-500/90 px-0.5 text-background-50">
            {p.part}
          </mark>
        ) : (
          <span key={i}>{p.part}</span>
        ),
      )}
    </>
  );
}

export default function VideoCard({ video, keyword, onToggleFavorite, isFavorite }: VideoCardProps) {
  return (
    <Link
      to={`/detail/${video.id}`}
      className="group block overflow-hidden rounded-lg border border-background-200/70 bg-background-100 transition hover:border-primary-400/70"
    >
      <div className="relative aspect-[2/3] w-full overflow-hidden bg-background-200">
        <img
          src={video.cover}
          alt={`${video.title} 封面`}
          title={`${video.title} 在线观看`}
          className="h-full w-full object-cover object-top transition duration-500 group-hover:scale-105"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent opacity-80" />
        <span className="absolute left-2 top-2 rounded-md bg-black/60 px-2 py-0.5 text-[11px] text-foreground-900">
          {video.type}
        </span>
        <span className="absolute right-2 top-2 flex items-center gap-1 rounded-md bg-black/60 px-2 py-0.5 text-[11px] font-medium text-accent-400">
          <i className="ri-star-fill text-[10px]"></i>
          {video.rating.toFixed(1)}
        </span>
        <span className="absolute inset-0 flex items-center justify-center opacity-0 transition duration-300 group-hover:opacity-100">
          <span className="flex h-12 w-12 items-center justify-center rounded-full bg-primary-500 text-background-50">
            <i className="ri-play-fill text-2xl"></i>
          </span>
        </span>
      </div>
      <div className="p-3">
        <h3 className="truncate text-sm font-semibold text-foreground-950">
          <HighlightText text={video.title} keyword={keyword} />
        </h3>
        <div className="mt-2 flex items-center justify-between">
          <span className="flex flex-wrap gap-1 text-[11px] text-foreground-500">
            <span>{video.year}</span>
            <span className="text-foreground-300">·</span>
            <span>{video.region}</span>
          </span>
          {onToggleFavorite && (
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                onToggleFavorite(video.id);
              }}
              className={`flex h-6 w-6 cursor-pointer items-center justify-center rounded-md text-sm transition ${
                isFavorite
                  ? "text-primary-500"
                  : "text-foreground-500 hover:text-primary-400"
              }`}
              aria-label={isFavorite ? "取消收藏" : "收藏"}
            >
              <i className={isFavorite ? "ri-heart-fill" : "ri-heart-line"}></i>
            </button>
          )}
        </div>
      </div>
    </Link>
  );
}